import { theme } from '@Guidelines/Theme/theme';
import DocumentTemplateCardActions from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/DocumentTemplates/DocumentTemplateCard/DocumentTemplateCardActions';
import type DocumentTemplateView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';
import { GridItem } from '@verclam/grid-system';
import type DocumentTemplateVersionsListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionsListView';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type TemplateAssignmentsListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentsListView';
import type FacilityTemplateOverridesListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/FacilityTemplateOverridesListView';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

// A single module-level formatter: `Intl.DateTimeFormat` construction is costly, and the instance is
// stateless and reusable across every call.
const depositedOnFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

const cardStyle = css`
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
	opacity: 1;
`;

const headerRowStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
`;

const nameStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const detailStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly template: DocumentTemplateView;
	readonly organizationId: number;
	readonly templateVersions: DocumentTemplateVersionsListView;
	readonly organizationAssignments: TemplateAssignmentsListView;
	readonly facilityOverrides: FacilityTemplateOverridesListView;
	/** Resolves the gabarit's document type label — the type itself is only an identifier on the wire. */
	readonly documentTypes: DocumentTypesListView;
};

/**
 * One gabarit, in the "Gabarits" section. `description` is only rendered when present — the server
 * never sends an empty string, only `null` — and the version line reads the resolved current version,
 * never the version history (that lives behind "Historique").
 */
const DocumentTemplateCard: React.FC<Props> = ({
	template,
	organizationId,
	templateVersions,
	organizationAssignments,
	facilityOverrides,
	documentTypes,
}) => {
	const currentVersion = React.useMemo(
		() => templateVersions.findById(template.currentVersionId),
		[templateVersions, template.currentVersionId],
	);

	const isAssigned = React.useMemo(
		() =>
			organizationAssignments.list.some((assignment) => assignment.templateId === template.id) ||
			facilityOverrides.countForTemplate(template.id) > 0,
		[organizationAssignments, facilityOverrides, template.id],
	);

	return (
		<GridItem columnSize={{ xl: 1, lg: 2, md: 2 }} subgrid={'rows'} css={cardStyle}>
			<div css={headerRowStyle}>
				<h3 css={nameStyle}>{template.name}</h3>
				<ConditionalLoader data={template.isArchived}>
					<Tag value="Archivé" severity="secondary" />
				</ConditionalLoader>
			</div>
			<p css={detailStyle}>{documentTypes.labelOf(template.documentTypeId)}</p>

			<ConditionalLoader data={template.description !== null}>
				<p css={detailStyle}>{template.description}</p>
			</ConditionalLoader>

			<ConditionalLoader data={currentVersion}>
				{(props) => (
					<p css={detailStyle}>
						Version {props.versionNumber} · déposée le{' '}
						{depositedOnFormatter.format(new Date(props.createdAt))} · {props.fieldCount}{' '}
						variables · {props.originalFilename}
					</p>
				)}
			</ConditionalLoader>

			<DocumentTemplateCardActions
				template={template}
				isAssigned={isAssigned}
				organizationId={organizationId}
			/>
		</GridItem>
	);
};

export default DocumentTemplateCard;
